import { I3dModel, IAsset, IData, IFilesApiObject, IFilesApiProduct as IFilesAPIProduct, IManufacturer, IMaterial, IPreviewPicture } from '../../@types/FilesAPIModels';
import { IAttribute } from './AttributeModel';

/**
 * Class to create a product model that reflects a single product that was retrieved from the imsFilesApi.
 * Implements the IProduct interface.
 *
 * @param {number} id numerical identifier of the product.
 * @param {number} ownerId numerical identifier of the product owner.
 * @param {boolean} publicFlag boolean flag to indicate if this product will be publicly available to other users.
 * @param {string} productId string identifier of the product.
 * @param {string} name string name of the product.
 * @param {string} description string description of the product.
 * @param {IManufacturer} manufacturer object representing the manufacturer of the product.
 * @param {boolean} isFavorite boolean flag to indicate if this product was favorited by the user.
 * @param {string[]} links optional array containing all related links of the product.
 * @param {string[]} attributes optional array containing all related attributes of the product.
 * @param {IAsset[]} assets optional array containing all related assets of the product.
 * @param {IFilesApiObject} object optional IObject object representing the category of the product with its name and id.
 * @param {IData[]} data optional IData array containing all other related data of this product.
 * @param {string} previewImagePath string file path for the preview image of this product.
 * @param {IPreviewPicture} previewPicture  object containing all data related to the preview image.
 * @param {IMaterial[]} materialAssets  array containing possible material assets.
 * @param {I3dModel[]} modelAssets  array containing possible model assets.
 * @param {number} width optional parameter width of that product. (in mm)
 * @param {number} length optional parameter length of that product. (in mm)
 */
class FilesAPIProductModel implements IFilesAPIProduct {
  id: number;

  ownerId: number;

  publicFlag: boolean;

  productId: string;

  name: string;

  description: string;

  manufacturer: IManufacturer;

  isFavorite: boolean;

  links: string[];

  attributes: IAttribute[];

  assets: IAsset[];

  objects: IFilesApiObject[];

  data: IData[];

  country: string;

  /**
   * This is a field, that is not directly reflecting the state of the imsFilesAPI products.
   * The field was added to provide an easily accessible string image path that will provide the
   * best available picture for the thumbnail in the product gallery.
   */
  previewImagePath: string;

  previewPicture: IPreviewPicture;

  materialAssets: IMaterial[];

  modelAssets: I3dModel[];

  depth: number;

  length: number;

  constructor(
    id: number,
    ownerId: number,
    publicFlag: boolean,
    productId: string,
    name: string,
    description: string,
    manufacturer: IManufacturer,
    isFavorite: boolean,
    links: string[],
    attributes: IAttribute[],
    assets: IAsset[],
    objects: IFilesApiObject[],
    data: IData[],
    previewImagePath: string,
    previewPicture: IPreviewPicture,
    materialAssets: IMaterial[],
    modelAssets: I3dModel[],
    depth?: number,
    length?: number,
    country?: string
  ) {
    this.id = id;
    this.ownerId = ownerId;
    this.publicFlag = publicFlag;
    this.productId = productId;
    this.name = name;
    this.description = description;
    this.manufacturer = manufacturer;
    this.isFavorite = isFavorite;
    this.links = links;
    this.attributes = attributes;
    this.assets = assets;
    this.objects = objects;
    this.data = data;
    this.previewImagePath = previewImagePath;
    this.previewPicture = previewPicture;
    this.materialAssets = materialAssets;
    this.modelAssets = modelAssets;
    this.depth = depth === undefined ? 0 : depth;
    this.length = length === undefined ? 0 : length;
    this.country = country === undefined ? "" : country;
    this.findDimensions();
  }

  // Method to check if dimensions are present and extract them from the rest of the objects data, when they are missing.
  public findDimensions = (): boolean => {
    // Instant return condition.
    if (this.depth !== 0 && this.length !== 0 && this.depth !== null && this.length !== null) {
      return true;
    }

    // Then we check if there is a material and try to extract the length and width from there.
    if (this.materialAssets.length !== 0) {
      for (const m of this.materialAssets) {
        if (m.width !== 0 && m.length !== 0 && m.width !== null && m.length !== null) {
          this.depth = m.width;
          this.length = m.length;
        }
      }
    }

    // Then we check if there is a model that should have our desired dimensions.
    if (this.modelAssets.length !== 0) {
      for (const m of this.modelAssets) {
        if (m.length !== 0 && m.depth !== 0 && m.length !== null && m.depth !== null) {
          this.length = m.length;
          this.depth = m.depth;
        }
      }
    }

    // Finally, we check if the values have changed and return the result of our check.
    if (this.length === 0 && this.depth === 0) {
      return false;
    }
    return true;
  };
}

export default FilesAPIProductModel;
